import { type TurfItem, type TurfRespondent } from './adapter';

export interface ReachCurveRow extends TurfReachResult {
  incrementalPct: number;
  itemAddedId: string;
  itemAddedLabel: string;
  itemAddedPinned: boolean;
  itemIds: string[];
  itemLabels: string[];
  size: number;
}

export interface TurfReachResult {
  frequency: number;
  hit: number;
  pct: number;
}

interface OptimizeBasketOptions {
  includeSpecialItems?: boolean;
  maxSize: number;
  pinnedItemIds?: string[];
}

export const calcReach = (
  respondents: TurfRespondent[] = [],
  itemIds: string[] = []
): TurfReachResult => {
  let hit = 0;
  let totalSelectionsAmongReached = 0;

  respondents.forEach((respondent) => {
    const selectedCount = itemIds.filter((id) => respondent.selectedItemIds.has(id)).length;

    if (selectedCount > 0) {
      hit += 1;
      totalSelectionsAmongReached += selectedCount;
    }
  });

  return {
    frequency: hit ? totalSelectionsAmongReached / hit : 0,
    hit,
    pct: respondents.length ? hit / respondents.length : 0,
  };
};

export const computeReachCurve = ({
  items = [],
  pinnedItemIds = [],
  respondents = [],
}: {
  items?: TurfItem[];
  pinnedItemIds?: string[];
  respondents?: TurfRespondent[];
}): ReachCurveRow[] =>
  optimizeBasket({
    items,
    options: {
      includeSpecialItems: true,
      maxSize: items.length,
      pinnedItemIds,
    },
    respondents,
  }).reachCurve;

export const optimizeBasket = ({
  items = [],
  options,
  respondents = [],
}: {
  items?: TurfItem[];
  options: OptimizeBasketOptions;
  respondents?: TurfRespondent[];
}): { itemIds: string[]; reachCurve: ReachCurveRow[] } => {
  const sourceIndexByItemId = new Map(items.map((item, index) => [item.id, index]));
  const eligibleItems = items.filter((item) => options.includeSpecialItems || !item.special);
  const eligibleItemIdSet = new Set(eligibleItems.map(({ id }) => id));
  const pinnedItemIdSet = new Set(options.pinnedItemIds ?? []);
  const pinnedItems = eligibleItems
    .filter((item) => pinnedItemIdSet.has(item.id))
    .sort((a, b) => {
      const reachDifference =
        calcReach(respondents, [b.id]).hit - calcReach(respondents, [a.id]).hit;

      if (reachDifference !== 0) return reachDifference;

      return (sourceIndexByItemId.get(a.id) ?? 0) - (sourceIndexByItemId.get(b.id) ?? 0);
    })
    .slice(0, options.maxSize);
  const sequence = pinnedItems.map(({ id }) => id);

  while (sequence.length < options.maxSize) {
    const availableItems = eligibleItems.filter(
      (item) => eligibleItemIdSet.has(item.id) && !sequence.includes(item.id)
    );

    if (!availableItems.length) break;

    const nextItem = availableItems
      .map((item) => ({
        item,
        reach: calcReach(respondents, [...sequence, item.id]),
      }))
      .sort((a, b) => {
        const reachDifference = b.reach.hit - a.reach.hit;

        if (reachDifference !== 0) return reachDifference;

        const frequencyDifference = b.reach.frequency - a.reach.frequency;

        if (frequencyDifference !== 0) return frequencyDifference;

        return (
          (sourceIndexByItemId.get(a.item.id) ?? 0) - (sourceIndexByItemId.get(b.item.id) ?? 0)
        );
      })[0]?.item;

    if (!nextItem) break;

    sequence.push(nextItem.id);
  }

  return {
    itemIds: sequence,
    reachCurve: buildReachCurveRows({
      itemIds: sequence,
      items,
      pinnedItemIds: options.pinnedItemIds,
      respondents,
    }),
  };
};

const buildReachCurveRows = ({
  itemIds,
  items,
  pinnedItemIds = [],
  respondents,
}: {
  itemIds: string[];
  items: TurfItem[];
  pinnedItemIds?: string[];
  respondents: TurfRespondent[];
}): ReachCurveRow[] => {
  const itemById = new Map(items.map((item) => [item.id, item]));
  const pinnedItemIdSet = new Set(pinnedItemIds);
  let previousPct = 0;

  return itemIds.map((itemId, index) => {
    const activeItemIds = itemIds.slice(0, index + 1);
    const reach = calcReach(respondents, activeItemIds);
    const row = {
      ...reach,
      incrementalPct: reach.pct - previousPct,
      itemAddedId: itemId,
      itemAddedLabel: itemById.get(itemId)?.label ?? itemId,
      itemAddedPinned: pinnedItemIdSet.has(itemId),
      itemIds: activeItemIds,
      itemLabels: activeItemIds.map(
        (activeItemId) => itemById.get(activeItemId)?.label ?? activeItemId
      ),
      size: activeItemIds.length,
    };

    previousPct = reach.pct;

    return row;
  });
};
